import { ConceptsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { Concept } from "@storytell/types";
import { createResource, onCleanup, onMount, startTransition } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "~/lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type ConceptsService = ReturnType<typeof createConceptsService>;

export const createConceptsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
  conceptsSynced: () => void;
}) => {
  const client = useStorytellClient();

  const concepts = createEntitySyncManager<Concept & { userId: string }>(undefined, (concept) => [
    concept.name,
    concept.description,
    concept.summary,
  ]);

  const storage = createResourceStorage(
    () => concepts.all(),
    (value) => {
      concepts.hydrateFromSync(value || []);
      deps.conceptsSynced();
    },
  );

  const [conceptsData, { refetch }] = createResource(
    () => !isServer && deps.authReady() && deps.userId(),
    async (): Promise<(Concept & { userId: string })[]> => {
      const userId = deps.userId();
      if (!userId) return [];
      const request = ConceptsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch concepts sync", response.error);
        return [];
      }

      const data = Object.values(response.value.concepts).map((concept) => ({ ...concept, userId }));
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as (Concept & { userId: string })[],
      deferStream: true,
      storage: () => storage,
    },
  );

  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("user-context-changed", () => {
      refetch();
    });

    const interval = setInterval(
      () => {
        // TODO: Smarter way to trigger a concepts sync
        startTransition(() => {
          refetch();
        });
      },
      2 * 60 * 1000,
    );

    onCleanup(() => {
      unsubscribe();
      clearInterval(interval);
    });
  });

  // This is used to trigger a suspense boundary so SSR works properly
  // Don't use it for data (that's why it's returned as undefined)
  const suspenseTrigger = () => (conceptsData() ? undefined : undefined);

  return {
    suspenseTrigger,
    refetch,
    ...concepts,
  };
};
